import {Component, ComponentFactoryResolver, Input, OnDestroy, OnInit, SimpleChanges, ViewChild} from '@angular/core';
import {ConfirmationService, MessageService} from 'primeng/api';
import {Subject, takeUntil} from 'rxjs';
// Service
import {EditableTableService} from './service/editable-table.service';
import {BroadcastService, ModalService, ModalSize} from "@cv-invision/core";
import {GenericFormService} from '@cv-invision/cv.invision-views.ux';
// Model
import {IntegrationsUrlParamsModel} from '../../../models/integrations-url-params.model';
import {EditableTableActions} from './model/editable-table.model';
// Components
import {CreateUpdateFormComponent} from "../create-update-form/create-update-form.component";
import {Table} from 'primeng/table';

@Component({
	selector: 'cv-editable-table',
	templateUrl: 'editable-table.component.html',
	styleUrls: ['./editable-table.component.scss'],
	providers: [ConfirmationService, MessageService]
})
export class EditableTableComponent implements OnInit, OnDestroy {
	@Input() label: string;
	@Input() modalSize: ModalSize = ModalSize.MD;
	@Input() model: any;
	@Input() formBuilderConfig: any;
	@ViewChild('dt') dataTable: any;
	@Input() tableConfig: IntegrationsUrlParamsModel;

	public integrationParams = new IntegrationsUrlParamsModel()
	public integrationExtraData: any = {};
	public defaultModel: any = {
		disabled: false,
		getValue: (field: any, row: any) => {
			return row[field.key];
		}
	}
	public data: any = [];
	clonedData: any = [];
	searchField: string;

	get cellWidth(): number {
		return 100 / (this.model.length + 1);
	}

	private atComponentDestroy: Subject<any> = new Subject<any>();

	constructor(
		public confirmationService: ConfirmationService,
		public messageService: MessageService,
		protected service: EditableTableService,
		protected modalService: ModalService,
		protected componentFactoryResolver: ComponentFactoryResolver,
		protected broadcast: BroadcastService,
		public formService: GenericFormService
	) {
	}

	ngOnInit() {
		this.searchField = '';
		this.model = this.model.map((tmpModel: any) => {
			return { ...this.defaultModel, ...tmpModel }
		});

		this.formService.submitObserver
			.pipe(takeUntil(this.atComponentDestroy))
			.subscribe((data: any) => {
				if (data.submit) {
					const row = data.data;
					if (data.isEditing) {
						this.onRowEditSave(row);
					} else {
						if ('saveAsObject' in this.integrationExtraData && this.integrationExtraData.saveAsObject) {
							this.saveModel(row);
						} else {
							this.onRowSave(row);
						}
					}
				}
			});

		this.formService.onKeyFilterOptions$
			.pipe(takeUntil(this.atComponentDestroy))
			.subscribe((data: any) => {
				const field = data.field;
				if (data && data.eventName === 'OnSelect') {
					this.formService
						.getDataByUrl(field.UIOptions.Url, field.UIOptions.FilterName, data.event.query)
						.pipe(takeUntil(this.atComponentDestroy))
						.subscribe(result => {
							this.formBuilderConfig[field.Name].options = result;
						});
				}
			});
	}

	/**
	 * Detect changes on values variable in order to option selected
	 * @param changes
	 */
	ngOnChanges(changes: SimpleChanges) {
		if (changes['model']) {
			this.model = changes['model'].currentValue.map((tmpModel: any) => {
				return { ...this.defaultModel, ...tmpModel }
			});
		}
		if (changes['params'] ||
			(
				changes['extraData'] &&
				'isExternalIntegration' in changes['extraData'].currentValue) &&
				changes['extraData'].currentValue.isExternalIntegration) {
			this.dataTable?.reset();
			this.searchField = '';
			this.loadData();
		}
	}

	/**
	 * Set the integration params
	 *
	 * @param params
	 */
	setIntegrationParamsData(params: IntegrationsUrlParamsModel): void {
		Object.assign(this.integrationParams, params);
	}

	/**
	 * Set extraData used in the model
	 *
	 * @param data
	 */
	setIntegrationExtraData(data: any): void {
		Object.assign(this.integrationExtraData, data);
	}

	/**
	 * Get the data from the service
	 */
	loadData() {
		this.service
			.getData(this.integrationParams)
			.pipe(takeUntil(this.atComponentDestroy))
			.subscribe(result => {
				this.data = result.data ? result.data : result;
				this.data = this.data.map((row: any, index: any) => {
					let item = row;

					if(this.integrationParams.externalSingleData){
						item = {};
						item[this.integrationParams.externalSingleDataKey] = row;	
					}

					return {
						...{id: index},
						...item,
					};
				});
			});
	}

	/**
	 * Trigger the field onChange
	 *
	 * @param $event
	 * @param field
	 * @param row
	 */
	onChange($event: any, field: any, row?: any): void {
		if (field.onChange) {
			field.onChange($event, row, this.model, this.data, this.integrationExtraData);
		}
	}

	/**
	 * On RowEdit, add the modified data into the cloned array.
	 * @param row
	 */
	onRowEditInit(row: any) {
		if ('editInModal' in this.integrationExtraData && this.integrationExtraData.editInModal) {
			this.addEditRecord(true, row);
		} else {
			this.clonedData[row.id] = { ...row };
		}

	}

	/**
	 * Save the data to the API
	 *
	 * @param row
	 */
	onRowEditSave(row: any): void {
		// Find the index of the row being edited
		const rowIndex = this.data.findIndex((r: any) => r.id === row.id);

		// If row exists, update it in the data array
		if (rowIndex >= 0) {
			this.data[rowIndex] = { ...row };
			delete this.clonedData[row.id]; // Remove the row from cloned data
		}

		// Check if the data needs to be saved as an object or as part of the array
		let data = this.data;
		if ('saveAsObject' in this.integrationExtraData && this.integrationExtraData.saveAsObject) {
			data = row;
		}

		// Save the updated data to the API
		this.service
			.saveData(this.integrationParams, data)
			.pipe(takeUntil(this.atComponentDestroy))
			.subscribe({
				next: result => {
					this.loadData(); // Reload the table data after saving
					this.messageService.add({
						severity: 'success',
						summary: 'Success',
						detail: `${this.label} was updated`
					});
				},
				error: error => {
					console.error('Error while saving row:', error);
					// Optionally, restore the cloned data in case of an error
					this.data[rowIndex] = { ...this.clonedData[row.id] };
				}
			});
	}


	/**
	 *
	 * @param row
	 */
	onRowSave(row: any): void {
		this.data.push(row);
		this.service
			.saveData(this.integrationParams, this.data)
			.pipe(takeUntil(this.atComponentDestroy))
			.subscribe({
				next: result => {
					this.loadData();
					this.messageService.add({
						severity: 'success',
						summary: 'Success',
						detail: `${this.label} was updated`
					});
				},
				error: error => {
					console.error(error);
				}
			});
	}

	/**
	 * Send a broadcast message when a ROW action it's triggered
	 *
	 * @param row
	 */
	onRowAction(action: string, row: any): void {
		this.broadcast.broadcast({
			name: EditableTableActions.TABLE_ROW_ACTION,
			event: {
				type: action,
				row
			}
		})
	}

	/**
	 * Show delete confirmation service
	 *
	 * @param row
	 */
	onShowDeleteConfirm(row: any) {
		this.confirmationService.confirm({
			message: 'Are you sure that you want to proceed?',
			header: 'Confirmation',
			icon: 'pi pi-exclamation-triangle',
			accept: () => {
				if (this.integrationParams.section === 'organizations' || ('saveAsObject' in this.integrationExtraData && this.integrationExtraData.saveAsObject)) {
					this.deleteModel(row);
				} else {
					this.deleteRow(row);
				}

			},
			// reject: () => {
			// }
		});
	}

	/**
	 * Save the data to the API on delete
	 *
	 * @param row
	 */
	deleteRow(row: any) {
		this.data = this.data.filter((item: { id: any; }) => item.id !== row.id);

		this.service
			.saveData(this.integrationParams, this.data)
			.pipe(takeUntil(this.atComponentDestroy))
			.subscribe({
				next: result => {
					this.messageService.add({
						severity: 'success',
						summary: 'Success',
						detail: `${this.label} was deleted`
					});
				},
				error: error => {
					console.error(error);
				}
			});
	}

	/**
	 * Remove changes from cloned array if edit is cancelled
	 * @param row
	 * @param index
	 */
	onRowEditCancel(row: any, index: number) {
		this.data[index] = this.clonedData[row.id];
		delete this.clonedData[row.id];
	}

	ngOnDestroy(): void {
		this.atComponentDestroy.next({});
		this.atComponentDestroy.unsubscribe();
	}

	/**
	 * Event to open modal to add or edit records
	 */
	addEditRecord(isEditing: boolean, data?: any): void {
		this.modalService
			.open({
				componentFactoryResolver: this.componentFactoryResolver,
				component: CreateUpdateFormComponent,
				data: {
					isEditing: isEditing,
					modelData: data,
					formBuilderConfig: {...this.formBuilderConfig},
					integrationExtraData: this.integrationExtraData,
					integrationParams: this.integrationParams,
					label: this.label
				},
				modalConfiguration: {
					title: `Add ${this.label}`,
					modalSize: this.modalSize || ModalSize.MD
				}
			})
			.pipe(takeUntil(this.atComponentDestroy))
			.subscribe(result => {
				//
			});
	}

	saveModel(row: any): void {
		this.service
			.savePostData(this.integrationParams, row)
			.pipe(takeUntil(this.atComponentDestroy))
			.subscribe({
				next: result => {
					this.loadData();
					this.messageService.add({
						severity: 'success',
						summary: 'Success',
						detail: `${this.label} was updated`
					});
				},
				error: error => {
					console.error(error);
				}
			});
	}

	deleteModel(row: any) {
		this.service
			.deleteModel(this.integrationParams, row)
			.pipe(takeUntil(this.atComponentDestroy))
			.subscribe({
				next: result => {
					this.loadData();
					this.messageService.add({
						severity: 'success',
						summary: 'Success',
						detail: `${this.label} was deleted`
					});
				},
				error: error => {
					console.error(error);
				}
			});
	}

	/**
	 * Get fields for global filter
	 * @returns
	 */
	getGlobalFilters(): Array<any> {
		return this.model.map((field: { key: any; })  => field.key);
	}

	/**
	 * Apply Global Filter
	 * @param $event
	 * @param table
	 */
	applyGlobalFilter($event: any, table: Table): void {
		table.filterGlobal($event.target.value, 'contains');
	}

	
}
